import { FragmentNode } from './core/fragment-node.js';
import { fetchFragmentMetadata } from './utils/fetch-fragment-metadata.js';
function engineError(message) {
    return new Error(`[Engine]: ${message}`);
}
/**
 * Engine for TWS applications.
 * Responsible to hold fragment-node instances, and render page content.
 */
class Engine {
    constructor(args) {
        this.fragments = new Map();
        this.renderer = args.renderer;
        this.metadataStore = args.metadataStore;
    }
    /**
     * Discard the fragment-node instance associated to the instanceUUID.
     * The instance will be destroyed, and its resources as well.
     *
     * @param instanceUUID - The fragment-node instance identifier.
     */
    discardFragment(instanceUUID) {
        const fragment = this.fragments.get(instanceUUID);
        if (fragment) {
            fragment.destroy();
            this.fragments.delete(instanceUUID);
        }
    }
    /**
     * Retrieves the fragment metadata from MetadataStore.
     *
     * @param name - The fragment name.
     */
    async getFragmentMetadata(name) {
        if (this.metadataStore.has(name)) {
            return this.metadataStore.get(name);
        }
        try {
            const { metadata } = await fetchFragmentMetadata(name);
            this.registerMetadata(name, metadata);
            return this.metadataStore.get(name);
        }
        catch {
            return null;
        }
    }
    /**
     * Retrieves a fragment.
     *
     * @param instanceUUID - The fragment instace uuid.
     */
    getFragment(instanceUUID) {
        return this.fragments.get(instanceUUID);
    }
    /**
     * Gets or creates a fragment-node instance, based on the fragment name and instance identifier.
     *
     * The engine will hold the fragment-node instance, that can be discarded through Engine#discardFragment.
     * It's not allowed to have more than one instance for the same instanceUUID.
     *
     * Note: It does not validate if the fragment exists into the MetadataStore.
     *
     * @param options - Options for the fragment that must be provided.
     */
    async provideFragment(options) {
        const { contextData, fragment, instanceUUID, root, submittable } = options;
        let fragmentNode = this.fragments.get(instanceUUID);
        if (fragmentNode) {
            return fragmentNode;
        }
        const metadata = await this.getFragmentMetadata(fragment);
        if (!metadata) {
            throw engineError(`Could not get metadata for fragment '${fragment}'.`);
        }
        fragmentNode = new FragmentNode({
            contextData,
            engine: this,
            instanceUUID,
            metadata,
            name: fragment,
            root: root ?? null,
            submittable,
        });
        this.fragments.set(instanceUUID, fragmentNode);
        return fragmentNode;
    }
    /**
     * Registers a metadata into MetadataStore instance.
     * The name is the key to identify the fragment when executing Engine#getFragmentMetadata and Engine#provideFragment.
     *
     * @param name - The fragment name.
     * @param metadata - The fragment metadata.
     * @throws when a fragment with the provided name already exists.
     */
    registerMetadata(name, metadata) {
        this.metadataStore.register(name, metadata);
    }
    /**
     * Renders the node using the engine's held instance or else the optional argument.
     *
     * @param node - The node that must be rendered.
     * @param renderer - An optional renderer that should priority over the engine's one.
     */
    render(node, renderer) {
        const theRenderer = renderer || this.renderer;
        return theRenderer.fromMetadata(node);
    }
}
export { Engine, };
//# sourceMappingURL=engine.js.map